import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/Button";
import { Icon } from "../app/Icon";
import { VerifiedPill, Tags } from "./HeroDetail";
import { useSaved } from "../saved/SavedContext";

/** Bookmark toggle (server-synced across devices). */
export function SaveButton({ kind, id }: { kind: "jobs" | "loads"; id: string }) {
  const { isSaved, toggle } = useSaved();
  const saved = isSaved(kind, id);
  return (
    <Button
      variant="ghost"
      aria-pressed={saved}
      onClick={(e) => { e.preventDefault(); void toggle(kind, id); }}
      className={saved ? "text-accent-strong" : ""}
    >
      <span className="flex items-center gap-1.5">
        <Icon name={saved ? "verify" : "folder"} className="h-4 w-4" />
        {saved ? "Saved" : "Save"}
      </span>
    </Button>
  );
}

/** A rich marketplace list card: title + verified pill + meta + tags + a compact
 *  spec row + Save / View actions. Used by the job and load boards. */
export function ResultCard({
  kind, id, to, title, verified, meta, tags, specs,
}: {
  kind: "jobs" | "loads";
  id: string;
  to: string;
  title: string;
  verified?: boolean;
  meta: ReactNode;
  tags?: (string | null | undefined)[];
  specs: [string, ReactNode][];
}) {
  const rows = specs.filter(([, v]) => v != null && v !== "");
  return (
    <div className="rounded-card border border-line bg-surface p-5 transition-colors hover:border-accent/60">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-1.5">
          <Link to={to} className="font-display text-xl font-black tracking-tight hover:text-accent-strong">{title}</Link>
          <p className="text-sm text-muted">{meta}</p>
        </div>
        {verified && <VerifiedPill />}
      </div>

      {tags && tags.filter(Boolean).length > 0 && <div className="mt-3"><Tags items={tags} /></div>}

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:flex lg:gap-x-8">
          {rows.map(([k, v]) => (
            <div key={k} className="grid gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted">{k}</span>
              <span className="font-mono text-sm font-bold text-ink">{v}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <SaveButton kind={kind} id={id} />
          <Link to={to}>
            <Button>View ›</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
